import { zodResolver } from '@hookform/resolvers/zod';
import { CargoType, FreightService, FreightStatus } from '@pelicargo/types';
import { PaymentStatus, ShipmentStatus } from '@prisma/client';
import { useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { getDifferences } from '../../utils/findChangedKeys';
import { RequestInput } from './useRequest';

type UseRequestForm = { methods: UseFormReturn<RequestInput> };

const validationSchema = z.object({
  arrive_by: z.coerce.date().nullish(),
  confirmed_at: z.coerce.date().nullish(),
  dropoff_by: z.coerce.date().nullish(),

  cargo_type: z.nativeEnum(CargoType).nullish(),

  service_type: z.array(z.string()).min(1, {
    message: 'At least one service type is required',
  }),
  freight_services: z.array(z.nativeEnum(FreightService)).min(1, {
    message: 'At least one freight service is required',
  }),
  is_known_shipper: z.boolean().nullish(),
  is_pre_screened: z.boolean().nullish(),
  requires_direct_flight: z.boolean().nullish(),
  additional_information: z.string().nullish(),

  original_weight_unit: z.enum(['kg', 'lb', '']).optional(),
  original_dimension_unit: z.enum(['cm', 'in', '']).optional(),

  gross_weight: z.coerce.number().nullish(),
  chargeable_weight: z.coerce.number().nullish(),

  freight_status: z.nativeEnum(FreightStatus).nullish(),

  // Perishable
  cargo_cooling_range: z.string().nullish(),
  cargo_cooling: z.string().nullish(),

  // Dangerous Goods
  un_number: z.coerce.string().nullish(),
  class: z.coerce.number().nullish(),
  packing_group: z.string().nullish(),
  aircraft: z.string().nullish(),

  status: z.string().nullish(),
  shipment_status: z.nativeEnum(ShipmentStatus).nullish(),
  payment_status: z.nativeEnum(PaymentStatus).nullish(),
  required_fulfillment_partner_id: z.coerce.number().nullish(),
});

export const useRequestForm = (data?: Partial<RequestInput>): UseRequestForm => {
  const { reset, getValues, setValue, ...methods } = useForm<RequestInput>({
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
    defaultValues: data,
  });

  useEffect(() => {
    const updateFields = () => {
      const changes = getDifferences<RequestInput>(getValues(), data);
      if (!changes.length) return;

      changes.map(([key, value]) => setValue<any>(key, value));
    };

    if (data) updateFields();
  }, [data, reset, getValues, setValue]);

  return { methods: { ...methods, getValues, setValue, reset } };
};
